/**
 * Blocks Use Cases Factory
 *
 * This file CAN import from infrastructure layer
 * because Composition Root is the only place where dependencies are assembled.
 *
 * @module @reports/composition/factories/createBlocksUseCases
 */

import type { AxiosInstance } from 'axios';
import type { AuthService } from 'types/AuthService';
import type { BlocksUseCases } from '../types';

// Infrastructure imports (composition CAN import from infrastructure)
import { createReportsHttpClient } from '@reports/infrastructure/api';
import { createReportApiMapper } from '@reports/infrastructure/mappers';
import { createReportsApiRepository } from '@reports/infrastructure/repositories';

// Use cases imports
import { fetchChartBlockDataUseCase, fetchTableBlockDataUseCase } from '@reports/application/use-cases';

/**
 * Creates all block use cases with injected dependencies.
 *
 * This function is called ONCE during initialization.
 * Use cases are singletons reused by all hooks.
 */
export const createBlocksUseCases = (httpClient: AxiosInstance, authService: AuthService): BlocksUseCases => {
  // Create infrastructure components
  const reportsHttpClient = createReportsHttpClient({ httpClient, authService });
  const mapper = createReportApiMapper();

  // Create repository that implements the application port
  const apiPort = createReportsApiRepository({
    httpClient: reportsHttpClient,
    mapper,
  });

  // Compose use cases by injecting the repository
  return {
    fetchChartBlock: fetchChartBlockDataUseCase(apiPort),
    fetchTableBlock: fetchTableBlockDataUseCase(apiPort),
  };
};
